import { Divider, Stack, Typography } from '@mui/material'
import {
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type React from 'react'
import FooterButton from 'src/views/components/drawer/FooterButton'
import { useFormikProps } from './EditPlaceGroup.hooks'
import NameRow from './NameRow'
import ParentRow from './ParentRow'
import PlacesRow from './PlacesRow'

export interface FlatNode extends PlaceNode {
  path: number[]
}

export const flattenNodes = (
  nodes: PlaceNode[],
  path: number[] | undefined,
) => {
  const flatNodes: FlatNode[] = []
  if (nodes && nodes.length > 0) {
    nodes.forEach(n => {
      const node = n as PlaceNode
      flatNodes.push({ ...node, path: [...(path ?? [])] })
      if (node.nodes && node.nodes.length)
        flatNodes.push(
          ...flattenNodes(node.nodes as PlaceNode[], [
            ...(path ?? []),
            node.id,
          ]),
        )
    })
  }
  return flatNodes
}

interface EditPlaceGroupProps {
  nodes: PlaceNode[]
  activeNodeId?: string
  activeParentNodeId?: number
  onClose: () => void
}

const EditPlaceGroup: React.FC<EditPlaceGroupProps> = ({
  nodes,
  activeNodeId,
  activeParentNodeId,
  onClose,
}) => {
  const placeGroupNodes = flattenNodes(nodes, []).filter(
    ({ type }) => type === PlaceNodeTypeEnum.PlaceGroup,
  )
  const activeNode = placeGroupNodes.find(({ uuid }) => uuid === activeNodeId)
  const activeParentNode = placeGroupNodes.find(
    ({ id }) => id === activeParentNodeId,
  )
  const activeGroup = activeNode?.placeGroup

  const {
    handleChange,
    handleSubmit,
    values,
    setFieldValue,
    errors,
    touched,
    isPending,
  } = useFormikProps({
    activeNode,
    activeParentNode,
    onClose,
  })

  return (
    <Stack sx={{ minHeight: '100vh' }}>
      <Typography variant="h6" my={2} px={3}>
        {`現場グループの${activeGroup ? '編集' : '追加'}`}
      </Typography>
      <Divider />
      <Stack>
        <Stack px={3} py={2} sx={{ gap: 2 }}>
          <NameRow {...{ handleChange, values, touched, errors }} />
          <ParentRow
            nodes={nodes}
            activeNodeId={activeNodeId}
            activeParentNodeId={activeParentNodeId}
            setFieldValue={setFieldValue}
            values={values}
          />
          <PlacesRow
            nodes={nodes}
            activeNodeId={activeNodeId}
            activeParentNodeId={activeParentNodeId}
            setFieldValue={setFieldValue}
          />
        </Stack>
        <Stack px={3} py={2}>
          <FooterButton
            spinning={isPending}
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default EditPlaceGroup
